import { supabase } from '@/integrations/supabase/client';
import { getFreePlanLimits } from './freePlanLimitsService';
import { OPOSICIONES_BASIC_PLAN } from '@/lib/oposicionesAccess';

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  limit: number;
  isLimitReached: boolean;
}

export type ChatLimitMode = 'funcionarios' | 'opositores';

// In-memory cache to avoid redundant queries during the same session
let cachedResult: { userId: string; mode: ChatLimitMode; result: RateLimitResult; timestamp: number } | null = null;
const CACHE_TTL = 30_000; // 30 s — refrescamos rápido para que el contador diario reaccione
const BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT = 300;
const OPOSITOR_CHAT_MODES = [
  'oposiciones',
  'oposiciones_c1',
  'oposiciones_c2',
  'oposiciones_iipp',
  'oposiciones_guardia_civil',
  'oposiciones_policia_nacional',
];

/**
 * Invalida el cache para forzar una recalculación tras enviar un mensaje
 */
export function invalidateMessageLimitCache() {
  cachedResult = null;
}

/**
 * Verifica si el usuario puede enviar un mensaje según su plan
 * Plan FREE: N mensajes por periodo (configurable desde admin).
 *  - Funcionarios free: límite `funcionario_chat_messages` (10/día por defecto)
 *  - Opositores free: límite `chat_messages` (3/mes por defecto)
 * El contador mensual se reinicia el día 1.
 * Plan Academy Básico: 300 mensajes/mes en chats de oposiciones.
 * Otros planes: ilimitado.
 */
export async function checkUserMessageLimit(
  userId: string,
  plan: string,
  mode: ChatLimitMode = 'funcionarios'
): Promise<RateLimitResult> {
  if (plan === OPOSICIONES_BASIC_PLAN && mode === 'opositores') {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const { data: userChats, error: chatsError } = await supabase
      .from('chats')
      .select('id')
      .eq('user_id', userId)
      .in('chat_mode', OPOSITOR_CHAT_MODES);

    if (chatsError || !userChats || userChats.length === 0) {
      return { allowed: true, remaining: BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT, limit: BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT, isLimitReached: false };
    }

    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'user')
      .in('chat_id', userChats.map(c => c.id))
      .gte('created_at', startOfMonth.toISOString());

    if (error) {
      console.error('Error checking basic oposiciones message count:', error);
      return { allowed: true, remaining: BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT, limit: BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT, isLimitReached: false };
    }

    const messageCount = count || 0;
    const remaining = Math.max(0, BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT - messageCount);
    const isLimitReached = messageCount >= BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT;
    return { allowed: !isLimitReached, remaining, limit: BASIC_OPOSICIONES_MONTHLY_CHAT_LIMIT, isLimitReached };
  }

  // Solo verificar para plan FREE y Academy Básico; el resto es ilimitado.
  if (plan !== 'free') {
    return {
      allowed: true,
      remaining: Infinity,
      limit: Infinity,
      isLimitReached: false,
    };
  }

  // Return cached result if valid
  if (
    cachedResult &&
    cachedResult.userId === userId &&
    cachedResult.mode === mode &&
    Date.now() - cachedResult.timestamp < CACHE_TTL
  ) {
    return cachedResult.result;
  }

  const limits = await getFreePlanLimits();
  const FREE_PLAN_LIMIT =
    mode === 'funcionarios' ? limits.funcionario_chat_messages : limits.chat_messages;

  const targetChatModes =
    mode === 'funcionarios'
      ? ['funcionarios']
      : OPOSITOR_CHAT_MODES;

  try {
    // Query 1: get user's chat IDs filtrados por modo
    const { data: userChats, error: chatsError } = await supabase
      .from('chats')
      .select('id')
      .eq('user_id', userId)
      .in('chat_mode', targetChatModes);

    if (chatsError) {
      console.error('Error fetching user chats:', chatsError);
      return { allowed: true, remaining: FREE_PLAN_LIMIT, limit: FREE_PLAN_LIMIT, isLimitReached: false };
    }

    if (!userChats || userChats.length === 0) {
      const result = { allowed: true, remaining: FREE_PLAN_LIMIT, limit: FREE_PLAN_LIMIT, isLimitReached: false };
      cachedResult = { userId, mode, result, timestamp: Date.now() };
      return result;
    }

    // Query 2: count user messages across all chats during the current month.
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'user')
      .in('chat_id', userChats.map(c => c.id))
      .gte('created_at', startOfMonth.toISOString());

    if (error) {
      console.error('Error checking message count:', error);
      return { allowed: true, remaining: FREE_PLAN_LIMIT, limit: FREE_PLAN_LIMIT, isLimitReached: false };
    }

    const messageCount = count || 0;
    const remaining = Math.max(0, FREE_PLAN_LIMIT - messageCount);
    const isLimitReached = messageCount >= FREE_PLAN_LIMIT;

    const result: RateLimitResult = {
      allowed: !isLimitReached,
      remaining,
      limit: FREE_PLAN_LIMIT,
      isLimitReached,
    };

    // Cache the result
    cachedResult = { userId, mode, result, timestamp: Date.now() };
    return result;
  } catch (error) {
    console.error('Error in checkUserMessageLimit:', error);
    return { allowed: true, remaining: FREE_PLAN_LIMIT, limit: FREE_PLAN_LIMIT, isLimitReached: false };
  }
}

/**
 * Obtiene el número de mensajes restantes para un usuario
 */
export async function getRemainingMessages(
  userId: string,
  plan: string,
  mode: ChatLimitMode = 'funcionarios'
): Promise<number> {
  const result = await checkUserMessageLimit(userId, plan, mode);
  return result.remaining;
}
